import React, {useState, useEffect} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiX, FiPieChart, FiPlus, FiMinus, FiInfo, FiTrendingUp, FiClock} = FiIcons;

function RetirementForm({retirementPlan, userProfile, onSubmit, onClose}) {
  const [formData, setFormData] = useState({
    ...retirementPlan,
    // Defaults from the profile's "Expected Monthly Expense (Post-Retirement)"
    // until the user has explicitly customized this plan's own value.
    monthlyIncomeRequired: retirementPlan.monthlyIncomeRequired || userProfile.postRetirementMonthlyExpense || 0,
    whatIfPercentage: retirementPlan.whatIfPercentage || 40,
    taggedAssets: retirementPlan.taggedAssets || [],
    // NEW: Editable retirement parameters
    lifeExpectancy: retirementPlan.lifeExpectancy || userProfile.lifeExpectancy || 80,
    preRetirementReturn: retirementPlan.preRetirementReturn || userProfile.returnExpectations?.longTerm || 12,
    postRetirementReturn: retirementPlan.postRetirementReturn || 7
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({...prev, [field]: value}));
  };

  const addTaggedAsset = () => {
    if (formData.taggedAssets.length < 3) {
      setFormData(prev => ({
        ...prev,
        taggedAssets: [...prev.taggedAssets, {assetKey: '', amount: 0, expectedReturn: 0}]
      }));
    }
  };

  const removeTaggedAsset = (index) => {
    setFormData(prev => ({
      ...prev,
      taggedAssets: prev.taggedAssets.filter((_, i) => i !== index)
    }));
  };

  const updateTaggedAsset = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      taggedAssets: prev.taggedAssets.map((asset, i) => 
        i === index ? {...asset, [field]: value} : asset
      )
    }));
  };

  // Generate asset options from profile
  const getAssetOptions = () => {
    const options = [];
    const investments = userProfile?.existingInvestments || {};
    const assets = userProfile?.assets || {};

    Object.entries(investments).forEach(([key, value]) => {
      if (value > 0) {
        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        options.push({value: key, label, amount: value, category: 'investments'});
      }
    });

    Object.entries(assets).forEach(([key, value]) => {
      if (value > 0 && key !== 'vehicles' && key !== 'insurance') {
        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        options.push({value: key, label, amount: value, category: 'assets'});
      }
    });

    return options;
  };

  const assetOptions = getAssetOptions();

  const handleAssetSelection = (index, assetKey) => {
    const selectedAsset = assetOptions.find(asset => asset.value === assetKey);
    if (selectedAsset) {
      updateTaggedAsset(index, 'assetKey', assetKey);
      updateTaggedAsset(index, 'amount', selectedAsset.amount);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value);
  };

  const whatIfPercentageOptions = [20, 40, 60, 80, 100];

  // Calculate projected retirement corpus from tagged assets with their specific returns
  const calculateProjectedCorpus = () => {
    const yearsToRetirement = userProfile.retirementAge - userProfile.currentAge;
    let totalProjected = 0;

    formData.taggedAssets.forEach(asset => {
      if (asset.amount > 0 && asset.expectedReturn > 0) {
        const futureValue = asset.amount * Math.pow(1 + asset.expectedReturn / 100, yearsToRetirement);
        totalProjected += futureValue;
      }
    });

    return totalProjected;
  };

  // Calculate retirement metrics based on current form inputs
  const calculateLiveMetrics = () => {
    const currentAge = userProfile.currentAge || 30;
    const retirementAge = userProfile.retirementAge || 60;
    const lifeExpectancy = formData.lifeExpectancy;
    const yearsToRetirement = Math.max(0, retirementAge - currentAge);
    const retirementDuration = Math.max(0, lifeExpectancy - retirementAge);
    const monthlyIncomeRequired = formData.monthlyIncomeRequired || 0;
    const inflationRate = userProfile.inflationRates?.general || 6;
    const preRetirementReturn = formData.preRetirementReturn;
    const postRetirementReturn = formData.postRetirementReturn;

    // Future value of required monthly income at retirement
    const futureMonthlyIncome = monthlyIncomeRequired * Math.pow(1 + inflationRate / 100, yearsToRetirement);
    const annualIncomeRequired = futureMonthlyIncome * 12;

    // Corpus required at retirement (using inflation-adjusted return)
    const realReturn = ((1 + postRetirementReturn / 100) / (1 + inflationRate / 100)) - 1;
    let corpusRequired = 0;

    if (realReturn > 0 && retirementDuration > 0) {
      corpusRequired = annualIncomeRequired * (1 - Math.pow(1 + realReturn, -retirementDuration)) / realReturn;
    } else {
      corpusRequired = annualIncomeRequired * retirementDuration;
    }

    // Future value of existing investments
    let existingCorpus = 0;
    if (formData.existingRetirementInvestments) {
      existingCorpus += formData.existingRetirementInvestments * Math.pow(1 + preRetirementReturn / 100, yearsToRetirement);
    }

    // Tagged assets with their specific returns
    if (formData.taggedAssets && Array.isArray(formData.taggedAssets)) {
      formData.taggedAssets.forEach(asset => {
        if (asset.amount > 0 && asset.expectedReturn > 0) {
          existingCorpus += asset.amount * Math.pow(1 + asset.expectedReturn / 100, yearsToRetirement);
        }
      });
    }

    const shortfall = Math.max(0, corpusRequired - existingCorpus);

    // Required SIP
    const monthlyRate = preRetirementReturn / 100 / 12;
    const totalMonths = yearsToRetirement * 12;
    let requiredSIP = 0;

    if (shortfall > 0 && totalMonths > 0) {
      if (monthlyRate > 0) {
        requiredSIP = shortfall / ((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate);
      } else {
        requiredSIP = shortfall / totalMonths;
      }
    }

    // Required Lumpsum
    const requiredLumpsum = shortfall > 0 
      ? shortfall / Math.pow(1 + preRetirementReturn / 100, yearsToRetirement)
      : 0;

    return {
      corpusRequired: Math.round(corpusRequired),
      existingCorpus: Math.round(existingCorpus),
      shortfall: Math.round(shortfall),
      requiredSIP: Math.round(requiredSIP),
      requiredLumpsum: Math.round(requiredLumpsum),
      yearsToRetirement,
      retirementDuration,
      futureMonthlyIncome: Math.round(futureMonthlyIncome)
    };
  };

  const projectedCorpus = calculateProjectedCorpus();
  const liveMetrics = calculateLiveMetrics();

  return (
    <motion.div
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      exit={{opacity: 0}}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
    >
      <motion.div
        initial={{scale: 0.9, opacity: 0}}
        animate={{scale: 1, opacity: 1}}
        exit={{scale: 0.9, opacity: 0}}
        className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
              <SafeIcon icon={FiPieChart} className="text-white text-sm" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Update Retirement Plan
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiX} className="text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Basic Retirement Parameters */}
              <div className="bg-blue-50 rounded-xl shadow-sm border border-blue-200 p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <SafeIcon icon={FiClock} className="text-blue-600" />
                  <h4 className="text-md font-semibold text-blue-900">Retirement Timeline</h4>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-2">
                      Life Expectancy (Years)
                    </label>
                    <input
                      type="number"
                      value={formData.lifeExpectancy || 0}
                      onChange={(e) => handleInputChange('lifeExpectancy', parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 border border-blue-300 bg-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="60"
                      max="120"
                      required
                    />
                    <p className="text-xs text-blue-700 mt-1">
                      Retirement Duration: {Math.max(0, formData.lifeExpectancy - userProfile.retirementAge)} years
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-white rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-700 font-medium mb-1">Current Age</p>
                      <p className="text-lg font-bold text-blue-900">{userProfile.currentAge}</p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-700 font-medium mb-1">Retirement Age</p>
                      <p className="text-lg font-bold text-blue-900">{userProfile.retirementAge}</p>
                    </div>
                  </div>

                  <div className="p-3 bg-white rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-700 font-medium mb-1">Years to Retirement</p>
                    <p className="text-lg font-bold text-blue-900">{liveMetrics.yearsToRetirement} years</p>
                  </div>
                </div>
              </div>

              {/* Return Expectations */}
              <div className="bg-green-50 rounded-xl shadow-sm border border-green-200 p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <SafeIcon icon={FiTrendingUp} className="text-green-600" />
                  <h4 className="text-md font-semibold text-green-900">Return Expectations</h4>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-2">
                      Pre-Retirement Return (% p.a.)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.preRetirementReturn || 0}
                      onChange={(e) => handleInputChange('preRetirementReturn', parseFloat(e.target.value) || 0)}
                      className="w-full px-4 py-3 border border-green-300 bg-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      min="0"
                      max="30"
                      required
                    />
                    <p className="text-xs text-green-700 mt-1">
                      Expected return during accumulation phase ({liveMetrics.yearsToRetirement} years)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-2">
                      Post-Retirement Return (% p.a.)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.postRetirementReturn || 0}
                      onChange={(e) => handleInputChange('postRetirementReturn', parseFloat(e.target.value) || 0)}
                      className="w-full px-4 py-3 border border-green-300 bg-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      min="0"
                      max="20"
                      required
                    />
                    <p className="text-xs text-green-700 mt-1">
                      Expected return during retirement ({liveMetrics.retirementDuration} years)
                    </p>
                  </div>

                  <div className="p-3 bg-white rounded-lg border border-green-200">
                    <p className="text-xs text-green-700 font-medium mb-1">Inflation Rate (from profile)</p>
                    <p className="text-lg font-bold text-green-900">{userProfile.inflationRates?.general || 6}% p.a.</p>
                  </div>
                </div>
              </div>

              {/* Income Requirements */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monthly Income Required (Today's Value) ₹
                  </label>
                  <input
                    type="number"
                    value={formData.monthlyIncomeRequired || 0}
                    onChange={(e) => handleInputChange('monthlyIncomeRequired', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formatCurrency(formData.monthlyIncomeRequired || 0)}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-1">
                    Defaulted from your profile's "Expected Monthly Expense (Post-Retirement)" — edit here to override just this plan.
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Future value at retirement: {formatCurrency(liveMetrics.futureMonthlyIncome)}/month
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Existing Retirement Investments ₹
                  </label>
                  <input
                    type="number"
                    value={formData.existingRetirementInvestments || 0}
                    onChange={(e) => handleInputChange('existingRetirementInvestments', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formatCurrency(formData.existingRetirementInvestments || 0)}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Monthly SIP ₹
                  </label>
                  <input
                    type="number"
                    value={formData.sipContribution || 0}
                    onChange={(e) => handleInputChange('sipContribution', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formatCurrency(formData.sipContribution || 0)}/month
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    One-time Lumpsum ₹
                  </label>
                  <input
                    type="number"
                    value={formData.lumpSumContribution || 0}
                    onChange={(e) => handleInputChange('lumpSumContribution', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formatCurrency(formData.lumpSumContribution || 0)} today
                  </p>
                </div>
              </div>

              {/* What-If Scenario */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-medium text-gray-700">What If Scenario</h4>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    If retirement cost increases by
                  </label>
                  <div className="grid grid-cols-5 gap-2">
                    {whatIfPercentageOptions.map(percent => (
                      <button
                        key={percent}
                        type="button"
                        className={`py-2 px-3 rounded-lg text-sm font-medium border ${
                          formData.whatIfPercentage === percent
                            ? 'bg-[#f58320] text-white border-[#f58320]'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                        onClick={() => handleInputChange('whatIfPercentage', percent)}
                      >
                        {percent}%
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Tagged Assets & Live Calculations */}
            <div className="space-y-6">
              {/* Live Calculation Results */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl shadow-sm border border-indigo-200 p-6">
                <h4 className="text-md font-semibold text-indigo-900 mb-4">Live Calculations</h4>
                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-3 border border-indigo-200">
                    <p className="text-xs text-indigo-700 font-medium mb-1">Corpus Required at Retirement</p>
                    <p className="text-2xl font-bold text-indigo-900">{formatCurrency(liveMetrics.corpusRequired)}</p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 border border-indigo-200">
                    <p className="text-xs text-indigo-700 font-medium mb-1">Existing Corpus (Future Value)</p>
                    <p className="text-xl font-bold text-indigo-900">{formatCurrency(liveMetrics.existingCorpus)}</p>
                  </div>

                  <div className={`bg-white rounded-lg p-3 border ${liveMetrics.shortfall > 0 ? 'border-red-300' : 'border-green-300'}`}>
                    <p className="text-xs font-medium mb-1" style={{color: liveMetrics.shortfall > 0 ? '#dc2626' : '#16a34a'}}>
                      {liveMetrics.shortfall > 0 ? 'Shortfall' : 'Surplus'}
                    </p>
                    <p className="text-xl font-bold" style={{color: liveMetrics.shortfall > 0 ? '#dc2626' : '#16a34a'}}>
                      {formatCurrency(Math.abs(liveMetrics.shortfall))}
                    </p>
                  </div>

                  {liveMetrics.shortfall > 0 && (
                    <>
                      <div className="bg-white rounded-lg p-3 border border-blue-200">
                        <p className="text-xs text-blue-700 font-medium mb-1">Required Monthly SIP</p>
                        <p className="text-2xl font-bold text-blue-900">{formatCurrency(liveMetrics.requiredSIP)}</p>
                      </div>

                      <div className="bg-white rounded-lg p-3 border border-purple-200">
                        <p className="text-xs text-purple-700 font-medium mb-1">OR Required Lumpsum (Today)</p>
                        <p className="text-xl font-bold text-purple-900">{formatCurrency(liveMetrics.requiredLumpsum)}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Tagged Assets */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-medium text-gray-700">Tag Existing Assets (Up to 3)</h4>
                  {formData.taggedAssets.length < 3 && (
                    <button
                      type="button"
                      onClick={addTaggedAsset}
                      className="flex items-center space-x-1 text-sm text-[#19b48d] hover:text-[#16a085]"
                    >
                      <SafeIcon icon={FiPlus} className="text-xs" />
                      <span>Add Asset</span>
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  {formData.taggedAssets.map((taggedAsset, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">Asset {index + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeTaggedAsset(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <SafeIcon icon={FiMinus} className="text-xs" />
                        </button>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Select Asset</label>
                          <select
                            value={taggedAsset.assetKey}
                            onChange={(e) => handleAssetSelection(index, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          >
                            <option value="">Choose an asset</option>
                            {assetOptions.map(asset => (
                              <option key={asset.value} value={asset.value}>
                                {asset.label} - {formatCurrency(asset.amount)}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Amount to Tag ₹</label>
                          <input
                            type="number"
                            value={taggedAsset.amount}
                            onChange={(e) => updateTaggedAsset(index, 'amount', parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            min="0"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {formatCurrency(taggedAsset.amount || 0)}
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Expected CAGR (%)</label>
                          <input
                            type="number"
                            step="0.1"
                            value={taggedAsset.expectedReturn}
                            onChange={(e) => updateTaggedAsset(index, 'expectedReturn', parseFloat(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            min="0"
                            max="50"
                            placeholder="Enter expected CAGR %"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Expected annual return for this asset
                          </p>
                        </div>

                        {taggedAsset.assetKey && taggedAsset.expectedReturn > 0 && (
                          <div className="p-2 bg-blue-50 rounded-lg">
                            <p className="text-xs text-blue-800">
                              <strong>Future Value at Retirement:</strong>{' '}
                              {formatCurrency(
                                taggedAsset.amount * Math.pow(
                                  1 + taggedAsset.expectedReturn / 100,
                                  userProfile.retirementAge - userProfile.currentAge
                                )
                              )}
                            </p>
                            <p className="text-xs text-blue-700 mt-1">
                              This asset will grow over {userProfile.retirementAge - userProfile.currentAge} years at {taggedAsset.expectedReturn}% CAGR.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {formData.taggedAssets.length > 0 && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                    <h4 className="text-sm font-semibold text-green-900 mb-3">Projected Retirement Corpus from Tagged Assets</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-green-700">Current Total Value:</span>
                        <span className="text-sm font-bold text-green-900">
                          {formatCurrency(formData.taggedAssets.reduce((sum, asset) => sum + (asset.amount || 0), 0))}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-green-700">Projected Value at Retirement:</span>
                        <span className="text-lg font-bold text-green-900">{formatCurrency(projectedCorpus)}</span>
                      </div>
                      <p className="text-xs text-green-700 mt-2">
                        Based on individual asset CAGR over {userProfile.retirementAge - userProfile.currentAge} years
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Summary Info */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-blue-800 mb-2">Calculation Summary</h4>
                <div className="text-xs text-blue-700 space-y-1">
                  <p>• Pre-retirement return: {formData.preRetirementReturn}%</p>
                  <p>• Post-retirement return: {formData.postRetirementReturn}%</p>
                  <p>• Inflation: {userProfile.inflationRates?.general || 6}%</p>
                  <p>• Years to retirement: {liveMetrics.yearsToRetirement}</p>
                  <p>• Retirement duration: {liveMetrics.retirementDuration} years</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200"
            >
              Update Retirement Plan
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

export default RetirementForm;